/**
 * stats-table-exam — 統計表考卷線上互動卷 + 純計分函式。
 *
 * 對應 math-exam.ts(數學考卷)同一套形狀:分大題作答 → 交卷即時計分 + 逐題對錯 +
 * 正解 + 錯題回流。每大題先**畫出該張表**(票價表 / 價目表 / 二維功課表),再列題 +
 * 填答格。純計分 `scoreStatsTableExam` / 答案正規化 `normalizeStatsAnswer` 抽出可單測
 * (無 DOM 依賴)。
 *
 * 視覺 = 學術風格設計系統(src/learn/ui):紙感頁殼 `pageShell` + 學術按鈕。
 * 專業介面鐵則:無音效、無 mascot、無彩光、無戲謔文案。
 *
 * 學習/遊戲分離:純學習側,只動學習數據,**絕不**碰 commanderXp / 戰鬥數值。
 */

import type {
  StatsTableExam,
  StatsTableSection,
  StatsTableQuestion,
  StatsTableData,
} from './stats-table-gen';
import { formatHM } from './stats-table-gen';
import { pageShell, academicButton } from '../ui/components';

// ───────────────────────────── 答案正規化 + 比對 ─────────────────────────────

/**
 * 正規化一個統計表答案字串以利寬鬆比對:
 *   - 全形數字 → 半形、全形標點 → 半形。
 *   - 去所有空白。
 *   - 「元 / 節」等單位字仍保留(數值題單位有意義),但「免費」「沒有」等保留原字。
 *   - 小寫化(英數)。
 * 例:'70 元' / '70元' / '７０元' → '70元';'國語' → '國語';'免費' → '免費'。
 *
 * 設計:單位寬鬆 —— 數值題接受「70」或「70 元」皆對(去單位後比數字),故另存
 * 去單位版供 fallback 比對(見 isAnswerCorrect)。
 */
export function normalizeStatsAnswer(raw: string): string {
  if (!raw) return '';
  let s = raw.trim();
  s = s.replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xff10 + 0x30));
  s = s.replace(/[，,、]/g, '、').replace(/[；;]/g, '、');
  s = s.replace(/\s+/g, '').toLowerCase();
  return s;
}

/** 去掉常見尾綴單位(元 / 節),供「數字寬鬆比對」。 */
function stripUnit(s: string): string {
  return s.replace(/(元|節)$/u, '');
}

/**
 * 一格是否答對(寬鬆比對):
 *   1. 正規化後完全相符 → 對。
 *   2. 去單位後相符(數值題「70」==「70 元」)→ 對。
 *   3. 「免費」== 「0」== 「0 元」(讀免費 = 0)→ 對。
 *   4. 多選名稱題(「米漿、豆漿」)順序不拘 → 排序後比。
 */
export function isAnswerCorrect(studentRaw: string | undefined, correct: string): boolean {
  if (studentRaw === undefined) return false;
  const s = normalizeStatsAnswer(studentRaw);
  const c = normalizeStatsAnswer(correct);
  if (s === '') return false;
  if (s === c) return true;
  // 數值寬鬆:去單位比。
  if (stripUnit(s) === stripUnit(c)) return true;
  // 免費 = 0。
  const freeNorm = (x: string): string => (x === '免費' || x === '0' || x === '0元' ? '0' : x);
  if (freeNorm(s) === freeNorm(stripUnit(c)) && (c === '免費' || stripUnit(c) === '0')) return true;
  if (freeNorm(stripUnit(s)) === '0' && (c === '免費' || stripUnit(c) === '0')) return true;
  // 多名稱題:用「、」分隔,排序後比(順序不拘)。
  if (c.includes('、')) {
    const sortKey = (x: string): string => x.split('、').filter(Boolean).sort().join('、');
    if (sortKey(s) === sortKey(c)) return true;
  }
  return false;
}

// ───────────────────────────── 純計分 ─────────────────────────────

/** 學生整卷作答:`"sectionIdx:questionIdx" → 字串`。 */
export type StatsExamAnswers = Record<string, string>;

/** 單題判定結果。 */
export interface StatsItemResult {
  itemId: string;
  correct: boolean;
  /** 正解顯示字串。 */
  correctLabel: string;
  /** 學生作答顯示字串(未作答 = 空)。 */
  chosenLabel: string;
  points: number;
  earned: number;
}

/** 整卷計分結果。 */
export interface StatsExamScore {
  score: number;
  total: number;
  correctCount: number;
  itemCount: number;
  results: StatsItemResult[];
}

/**
 * 純計分:逐題批改(每題一格)。可單測(無 DOM 依賴)。
 *
 * @param exam    一份統計表考卷。
 * @param answers 整卷作答(`"sectionIdx:questionIdx" → 字串`)。
 * @returns 得分 / 滿分 / 逐題結果。
 */
export function scoreStatsTableExam(
  exam: StatsTableExam,
  answers: StatsExamAnswers,
): StatsExamScore {
  const results: StatsItemResult[] = [];
  let score = 0;
  let total = 0;
  let correctCount = 0;
  let itemCount = 0;

  exam.sections.forEach((section, sIdx) => {
    section.questions.forEach((q, qIdx) => {
      itemCount += 1;
      total += section.pointsEach;
      const studentRaw = answers[`${sIdx}:${qIdx}`];
      const ok = isAnswerCorrect(studentRaw, q.answer);
      const earned = ok ? section.pointsEach : 0;
      if (ok) {
        correctCount += 1;
        score += section.pointsEach;
      }
      results.push({
        itemId: q.id,
        correct: ok,
        correctLabel: q.answer,
        chosenLabel: studentRaw && studentRaw.trim() !== '' ? studentRaw.trim() : '',
        points: section.pointsEach,
        earned,
      });
    });
  });

  return { score, total, correctCount, itemCount, results };
}

// ───────────────────────────── 表格 DOM 繪製 ─────────────────────────────

const TH = 'border:1px solid var(--ef-line,#000);padding:6px 10px;font-weight:700;background:var(--ef-paper,#f5f1e6);text-align:center;';
const TD = 'border:1px solid var(--ef-line,#000);padding:6px 10px;text-align:center;color:var(--ef-ink,#000);';

/** 畫一張統計表(三型分流)into 一個 table 元素。 */
function renderTable(table: StatsTableData): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'st-table-wrap';
  wrap.style.cssText = 'overflow-x:auto;margin:6px 0 4px;';

  const caption = document.createElement('div');
  caption.className = 'st-table-caption';
  caption.style.cssText = 'font-weight:700;font-size:15px;color:var(--ef-ink);margin-bottom:6px;';
  caption.textContent = table.caption;
  wrap.appendChild(caption);

  const t = document.createElement('table');
  t.className = `st-table st-table--${table.kind}`;
  t.setAttribute('data-testid', `st-table-${table.kind}`);
  t.style.cssText = 'border-collapse:collapse;font-size:14px;';

  if (table.kind === 'price') {
    const head = document.createElement('tr');
    for (const h of table.headers) {
      const th = document.createElement('th');
      th.style.cssText = TH;
      th.textContent = h;
      head.appendChild(th);
    }
    t.appendChild(head);
    for (const r of table.rows) {
      const tr = document.createElement('tr');
      const c1 = document.createElement('td');
      c1.style.cssText = TD;
      c1.textContent = r.name;
      const c2 = document.createElement('td');
      c2.style.cssText = TD;
      c2.textContent = r.display ?? `${r.value} ${table.unit}`;
      tr.append(c1, c2);
      t.appendChild(tr);
    }
  } else if (table.kind === 'menu') {
    const head = document.createElement('tr');
    for (const h of [table.groupAName, '價錢', table.groupBName, '價錢']) {
      const th = document.createElement('th');
      th.style.cssText = TH;
      th.textContent = h;
      head.appendChild(th);
    }
    t.appendChild(head);
    const n = Math.max(table.groupA.length, table.groupB.length);
    for (let i = 0; i < n; i++) {
      const tr = document.createElement('tr');
      const a = table.groupA[i];
      const b = table.groupB[i];
      const cells = [
        a ? a.name : '',
        a ? `${a.value} ${table.unit}` : '',
        b ? b.name : '',
        b ? `${b.value} ${table.unit}` : '',
      ];
      for (const txt of cells) {
        const td = document.createElement('td');
        td.style.cssText = TD;
        td.textContent = txt;
        tr.appendChild(td);
      }
      t.appendChild(tr);
    }
  } else if (table.kind === 'schedule') {
    // schedule:左上角空白 + 星期表頭;每列節次 + 各格科目。
    const head = document.createElement('tr');
    const corner = document.createElement('th');
    corner.style.cssText = TH;
    corner.textContent = '';
    head.appendChild(corner);
    for (const d of table.days) {
      const th = document.createElement('th');
      th.style.cssText = TH;
      th.textContent = d;
      head.appendChild(th);
    }
    t.appendChild(head);
    for (let p = 0; p < table.periods.length; p++) {
      const tr = document.createElement('tr');
      const rowHead = document.createElement('th');
      rowHead.style.cssText = TH;
      rowHead.textContent = table.periods[p];
      tr.appendChild(rowHead);
      for (let d = 0; d < table.days.length; d++) {
        const td = document.createElement('td');
        td.style.cssText = TD;
        td.textContent = table.grid[p][d];
        tr.appendChild(td);
      }
      t.appendChild(tr);
    }
  } else if (table.kind === 'quantity') {
    // quantity:一維(類別表頭列 + 數量列)。
    const head = document.createElement('tr');
    for (const c of table.categories) {
      const th = document.createElement('th');
      th.style.cssText = TH;
      th.textContent = c;
      head.appendChild(th);
    }
    t.appendChild(head);
    const valRow = document.createElement('tr');
    for (const v of table.counts) {
      const td = document.createElement('td');
      td.style.cssText = TD;
      td.textContent = `${v} ${table.unit}`;
      valRow.appendChild(td);
    }
    t.appendChild(valRow);
  } else if (table.kind === 'transit') {
    // transit:左上角「車次」+ 站名表頭;每列車次 + 各站時間(— = 過站不停)。
    const head = document.createElement('tr');
    const corner = document.createElement('th');
    corner.style.cssText = TH;
    corner.textContent = '車次';
    head.appendChild(corner);
    for (const s of table.stations) {
      const th = document.createElement('th');
      th.style.cssText = TH;
      th.textContent = s;
      head.appendChild(th);
    }
    t.appendChild(head);
    for (let r = 0; r < table.trains.length; r++) {
      const tr = document.createElement('tr');
      const rowHead = document.createElement('th');
      rowHead.style.cssText = TH;
      rowHead.textContent = table.trains[r];
      tr.appendChild(rowHead);
      for (let s = 0; s < table.stations.length; s++) {
        const td = document.createElement('td');
        td.style.cssText = TD;
        const cell = table.grid[r][s];
        td.textContent = cell === null ? '—' : formatHM(cell);
        tr.appendChild(td);
      }
      t.appendChild(tr);
    }
  } else if (table.kind === 'program-schedule') {
    // program-schedule:時間 + 節目兩欄(末項「結束」哨兵也列出,讓孩子能算最後節目時長)。
    const head = document.createElement('tr');
    for (const h of ['時間', '節目']) {
      const th = document.createElement('th');
      th.style.cssText = TH;
      th.textContent = h;
      head.appendChild(th);
    }
    t.appendChild(head);
    for (let r = 0; r < table.programs.length; r++) {
      const tr = document.createElement('tr');
      const c1 = document.createElement('td');
      c1.style.cssText = TD;
      c1.textContent = formatHM(table.times[r]);
      const c2 = document.createElement('td');
      c2.style.cssText = TD;
      c2.textContent = table.programs[r];
      tr.append(c1, c2);
      t.appendChild(tr);
    }
  } else {
    // fareMatrix:左上角「起站＼到站」+ 站名表頭;每列起站 + 各到站票價。
    const head = document.createElement('tr');
    const corner = document.createElement('th');
    corner.style.cssText = TH;
    corner.textContent = '起站＼到站';
    head.appendChild(corner);
    for (const s of table.stations) {
      const th = document.createElement('th');
      th.style.cssText = TH;
      th.textContent = s;
      head.appendChild(th);
    }
    t.appendChild(head);
    for (let i = 0; i < table.stations.length; i++) {
      const tr = document.createElement('tr');
      const rowHead = document.createElement('th');
      rowHead.style.cssText = TH;
      rowHead.textContent = table.stations[i];
      tr.appendChild(rowHead);
      for (let j = 0; j < table.stations.length; j++) {
        const td = document.createElement('td');
        td.style.cssText = TD;
        td.textContent = i === j ? '—' : `${table.matrix[i][j]} ${table.unit}`;
        tr.appendChild(td);
      }
      t.appendChild(tr);
    }
  }

  wrap.appendChild(t);
  return wrap;
}

// ───────────────────────────── 互動卷 UI ─────────────────────────────

/** 交卷結果回呼:錯題(供回流錯題本)。 */
export interface StatsExamWrong {
  question: StatsTableQuestion;
  sectionKind: StatsTableSection['kind'];
}

/**
 * Render 線上統計表考卷 into `container`。每大題畫表 + 列題 + 填答格 → 「交卷」即時
 * 計分 + 逐題對錯。
 *
 * @param container 掛載點。
 * @param exam      一份(產生)統計表考卷。
 * @param onDone    離開 / 完成回呼。
 * @param onSubmit  選填:交卷後回呼(帶分數 + 錯題),供錯題回流。
 */
export function runStatsTableExam(
  container: HTMLElement,
  exam: StatsTableExam,
  onDone: () => void,
  onSubmit?: (score: StatsExamScore, wrong: StatsExamWrong[]) => void,
): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: exam.title,
    subtitle: `${exam.scope} · 先看表,再在每格寫出答案,作答後按「交卷」看分數與正解。`,
    onBack: onDone,
    confirmOnBack: '離開後這張卷的作答不會保留,確定?',
  });

  const root = document.createElement('div');
  root.className = 'stats-table-exam';
  root.setAttribute('data-testid', 'stats-table-exam');
  root.style.cssText = 'display:flex;flex-direction:column;gap:22px;';
  shell.body.appendChild(root);

  const answers: StatsExamAnswers = {};
  let submitted = false;

  exam.sections.forEach((section, sIdx) => {
    const sectionEl = document.createElement('section');
    sectionEl.className = `st-section st-section--${section.kind}`;
    sectionEl.style.cssText = 'display:flex;flex-direction:column;gap:14px;';

    const head = document.createElement('div');
    head.className = 'st-section-head';
    head.style.cssText =
      'font-family:var(--ef-serif);font-weight:700;font-size:18px;color:var(--ef-ink);' +
      'border-left:4px solid var(--ef-accent,#2f6b4f);padding-left:10px;';
    head.textContent = `${section.label}、${section.title}`;
    sectionEl.appendChild(head);

    const instr = document.createElement('div');
    instr.className = 'st-section-instr';
    instr.style.cssText = 'font-size:14px;color:var(--ef-sub);line-height:1.6;';
    instr.textContent = section.instructions;
    sectionEl.appendChild(instr);

    // 畫表。
    sectionEl.appendChild(renderTable(section.table));

    // 列題 + 填答格。
    section.questions.forEach((q, qIdx) => {
      sectionEl.appendChild(renderQuestion(sIdx, qIdx, q, answers));
    });

    root.appendChild(sectionEl);
  });

  // ── 交卷 + 結果區 ──
  const actions = document.createElement('div');
  actions.className = 'st-actions';
  actions.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;margin-top:8px;';
  root.appendChild(actions);

  const resultEl = document.createElement('div');
  resultEl.className = 'st-result';
  resultEl.style.cssText = 'display:none;flex-direction:column;gap:10px;';
  root.appendChild(resultEl);

  const submitBtn = academicButton({
    label: '交卷',
    variant: 'primary',
    onClick: () => {
      if (submitted) return;
      submitted = true;
      const result = scoreStatsTableExam(exam, answers);
      renderResult(resultEl, result);
      submitBtn.disabled = true;
      submitBtn.classList.add('st-submit--done');
      resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (onSubmit) {
        const wrong: StatsExamWrong[] = [];
        let flat = 0;
        exam.sections.forEach((section) => {
          section.questions.forEach((q) => {
            const r = result.results[flat];
            flat += 1;
            if (r && !r.correct) wrong.push({ question: q, sectionKind: section.kind });
          });
        });
        onSubmit(result, wrong);
      }
    },
  });
  submitBtn.classList.add('st-submit');
  submitBtn.setAttribute('data-testid', 'st-submit');
  actions.appendChild(submitBtn);

  const doneBtn = academicButton({
    label: '完成',
    variant: 'secondary',
    onClick: () => {
      shell.unmount();
      onDone();
    },
  });
  doneBtn.classList.add('st-done');
  actions.appendChild(doneBtn);
}

/** 一題的容器(題號 + 題幹 + 一格輸入)。 */
function renderQuestion(
  sIdx: number,
  qIdx: number,
  q: StatsTableQuestion,
  answers: StatsExamAnswers,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'st-q';
  row.dataset.itemId = q.id;
  row.style.cssText =
    'display:flex;flex-direction:column;gap:8px;padding:12px 14px;' +
    'background:var(--ef-paper-bright,#fff);border:1px solid var(--ef-line,#d8d2c4);' +
    'border-radius:var(--ef-radius,8px);';

  const top = document.createElement('div');
  top.style.cssText = 'display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;';
  const num = document.createElement('span');
  num.style.cssText = 'font-weight:700;min-width:1.6em;color:var(--ef-ink);';
  num.textContent = `${qIdx + 1}.`;
  top.appendChild(num);
  const prompt = document.createElement('span');
  prompt.style.cssText = 'font-size:15px;color:var(--ef-ink);line-height:1.6;';
  prompt.textContent = q.prompt;
  top.appendChild(prompt);
  row.appendChild(top);

  const field = document.createElement('label');
  field.style.cssText = 'display:inline-flex;align-items:center;gap:6px;font-size:14px;color:var(--ef-sub);';
  const tag = document.createElement('span');
  tag.textContent = '答:';
  field.appendChild(tag);
  const input = document.createElement('input');
  input.type = 'text';
  input.className = `st-input st-input--${q.id}`;
  input.autocomplete = 'off';
  input.style.cssText =
    'min-width:7em;padding:6px 8px;font-size:15px;border:1px solid var(--ef-line,#d8d2c4);' +
    'border-radius:6px;background:var(--ef-paper-bright,#fff);color:var(--ef-ink);';
  answers[`${sIdx}:${qIdx}`] = '';
  input.addEventListener('input', () => {
    answers[`${sIdx}:${qIdx}`] = input.value;
  });
  field.appendChild(input);
  row.appendChild(field);

  return row;
}

/** 結果區:總分 + 逐題對錯 + 正解。 */
function renderResult(el: HTMLElement, result: StatsExamScore): void {
  el.innerHTML = '';
  el.style.display = 'flex';

  const headline = document.createElement('div');
  headline.className = 'st-score';
  headline.setAttribute('data-testid', 'st-score');
  headline.style.cssText =
    'font-family:var(--ef-serif);font-weight:700;font-size:24px;color:var(--ef-ink);' +
    'border-top:2px solid var(--ef-line,#d8d2c4);padding-top:14px;';
  headline.textContent = `分數:${result.score} / ${result.total}(答對 ${result.correctCount} / ${result.itemCount} 題)`;
  el.appendChild(headline);

  const list = document.createElement('div');
  list.className = 'st-result-list';
  list.style.cssText = 'display:flex;flex-direction:column;gap:8px;font-size:14px;';
  result.results.forEach((r, idx) => {
    const line = document.createElement('div');
    line.className = `st-result-line ${r.correct ? 'st-correct' : 'st-wrong'}`;
    const mark = r.correct ? '✓' : '✗';
    const chosen = r.chosenLabel || '(未作答)';
    line.style.color = r.correct ? 'var(--ef-ink)' : '#b03030';
    line.textContent = r.correct
      ? `${mark} 第 ${idx + 1} 題:${r.correctLabel}`
      : `${mark} 第 ${idx + 1} 題:你寫「${chosen}」→ 正解「${r.correctLabel}」`;
    list.appendChild(line);
  });
  el.appendChild(list);
}
